import type { SubmissionView } from '@thottbot/shared';

type Data = Record<string, unknown>;

function flatten(
  obj: unknown,
  prefix = '',
  out: Record<string, string> = {},
): Record<string, string> {
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    for (const [k, v] of Object.entries(obj as Data))
      flatten(v, prefix ? `${prefix}.${k}` : k, out);
    return out;
  }
  if (Array.isArray(obj)) {
    out[prefix] = obj
      .map((v) =>
        v && typeof v === 'object'
          ? (((v as Data).name as string) ?? JSON.stringify(v))
          : String(v),
      )
      .join(', ');
    return out;
  }
  if (obj !== undefined && obj !== null && obj !== '') out[prefix] = String(obj);
  return out;
}

/** Seed-managed data the form never edits: spawn coordinates, media URLs, ability ranks and trainer links. */
const HIDDEN_KEYS = /^(locations|iconUrl|imageUrl|mapUrl|minimapUrl|loadingScreenUrl|displayId|ranks|taughtBy|learnedFrom|teaches|startingFor)/;

export interface DiffRow {
  field: string;
  before: string | undefined;
  after: string | undefined;
}

/** Fields whose value differs between the live artifact and the proposal (creates list everything). */
export function diffRows(
  current: { name: string; data: Data } | null | undefined,
  proposedName: string,
  proposedData: Data,
): DiffRow[] {
  const before: Record<string, string> = current
    ? { name: current.name, ...flatten(current.data) }
    : {};
  const after: Record<string, string> = { name: proposedName, ...flatten(proposedData) };
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  return keys
    .filter((k) => !HIDDEN_KEYS.test(k) && before[k] !== after[k])
    .map((k) => ({ field: k, before: before[k], after: after[k] }));
}

export function SubmissionDiff({ submission }: { submission: SubmissionView }) {
  const rows = diffRows(
    submission.kind === 'edit' ? submission.current : null,
    submission.proposedName,
    submission.proposedData,
  );
  if (rows.length === 0)
    return <p className="muted">No field changes (only the note or verification flag).</p>;
  return (
    <div className="table-scroll">
      <table className="grid" data-testid="diff">
        <thead>
          <tr>
            <th>Field</th>
            {submission.kind === 'edit' && <th>Current</th>}
            <th>Proposed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.field}>
              <th>{r.field}</th>
              {submission.kind === 'edit' && <td className="muted">{r.before ?? ''}</td>}
              <td>{r.after ?? <span className="muted">(removed)</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
